import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ChevronLeft, Heart, MessageCircle, Trophy, TrendingDown, 
  Plus, Filter, Star, Clock, Users, Sparkles, ChevronDown,
  ArrowUpRight, Award
} from 'lucide-react';
import { formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessStories = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedStory, setExpandedStory] = useState(null);

  // Form state for creating new story
  const [formData, setFormData] = useState({
    title: '',
    child_age_at_onset: '',
    child_current_age: '',
    diagnosis: 'PANDAS/PANS',
    initial_symptoms: [],
    peak_severity: 8,
    current_severity: 2,
    journey_summary: '',
    treatments_that_helped: [],
    key_turning_points: '',
    advice_for_parents: '',
    time_to_improvement: '',
    is_anonymous: false
  });

  const symptomOptions = [
    'OCD thoughts', 'Anxiety', 'Tics', 'Rage episodes', 'Sleep issues',
    'Food restriction', 'Sensory sensitivities', 'Regression', 'Separation anxiety',
    'Depression', 'Hyperactivity', 'Brain fog', 'Motor changes'
  ];

  const treatmentOptions = [
    'Antibiotics (Amoxicillin)', 'Antibiotics (Azithromycin)', 'Steroids (Prednisone)',
    'IVIG', 'Plasmapheresis', 'T&A Surgery', 'Anti-inflammatories (Ibuprofen)',
    'Supplements (NAC)', 'Supplements (Omega-3)', 'Supplements (Vitamin D)',
    'Probiotics', 'Diet changes', 'CBT/Therapy', 'Low-dose naltrexone',
    'Other medications'
  ];

  useEffect(() => {
    fetchStories();
    fetchStats();
  }, [sortBy]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/success-stories?sort_by=${sortBy}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStories(data.stories || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/success-stories/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLike = async (storyId) => {
    try {
      const response = await fetch(`${API_URL}/api/success-stories/${storyId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Update local state
      setStories(stories.map(s => 
        s.id === storyId 
          ? { ...s, likes_count: data.likes_count, is_liked: data.is_liked }
          : s
      ));
    } catch (error) {
      console.error('Error liking story:', error);
    }
  };

  const handleSubmitStory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/success-stories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowCreateForm(false);
        fetchStories();
        setFormData({
          title: '',
          child_age_at_onset: '',
          child_current_age: '',
          diagnosis: 'PANDAS/PANS',
          initial_symptoms: [],
          peak_severity: 8,
          current_severity: 2,
          journey_summary: '',
          treatments_that_helped: [],
          key_turning_points: '',
          advice_for_parents: '',
          time_to_improvement: '',
          is_anonymous: false
        });
      }
    } catch (error) {
      console.error('Error creating story:', error);
    }
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const getSeverityColor = (severity) => {
    if (severity <= 2) return 'text-green-600 bg-green-100';
    if (severity <= 4) return 'text-lime-600 bg-lime-100';
    if (severity <= 6) return 'text-amber-600 bg-amber-100';
    if (severity <= 8) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-emerald-50 pb-20" data-testid="success-stories-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D] flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-amber-500" />
                  Treatment Success Stories
                </h1>
                <p className="text-sm text-[#57534E]">Hope and healing journeys from our community</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="share-story-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Share Your Story
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Banner */}
        {stats && (
          <Card className="bg-gradient-to-r from-primary/10 to-emerald-100 border-primary/20 rounded-xl p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="text-2xl font-bold text-primary">{stats.total_stories}</span>
                </div>
                <p className="text-sm text-[#57534E]">Stories Shared</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <TrendingDown className="w-5 h-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-600">{stats.average_improvement}%</span>
                </div>
                <p className="text-sm text-[#57534E]">Avg Improvement</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="text-2xl font-bold text-amber-600">{stats.average_peak_severity?.toFixed(1)}</span>
                </div>
                <p className="text-sm text-[#57534E]">Avg Peak Severity</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Award className="w-5 h-5 text-emerald-600" />
                  <span className="text-2xl font-bold text-emerald-600">{stats.average_current_severity?.toFixed(1)}</span>
                </div>
                <p className="text-sm text-[#57534E]">Avg Current Severity</p>
              </div>
            </div>
            
            {stats.top_treatments?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-primary/20">
                <p className="text-sm font-medium text-[#57534E] mb-2">Most Helpful Treatments:</p>
                <div className="flex flex-wrap gap-2">
                  {stats.top_treatments.slice(0, 5).map((t, i) => (
                    <span key={i} className="px-3 py-1 bg-white/70 rounded-full text-sm text-primary font-medium">
                      {t.treatment} ({t.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Sort Controls */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {[
              { value: 'recent', label: 'Most Recent' },
              { value: 'likes', label: 'Most Liked' },
              { value: 'improvement', label: 'Best Improvement' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setSortBy(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  sortBy === option.value
                    ? 'bg-primary text-white'
                    : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stories List */}
        {loading ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <p className="text-[#57534E]">Loading success stories...</p>
          </Card>
        ) : stories.length === 0 ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <Trophy className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No stories yet</h3>
            <p className="text-sm text-[#57534E] mb-4">Be the first to share your family's success story!</p>
            <Button onClick={() => setShowCreateForm(true)} className="bg-primary">
              Share Your Story
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {stories.map(story => (
              <Card 
                key={story.id} 
                className="bg-white rounded-xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all"
                data-testid={`story-card-${story.id}`}
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">{story.title}</h3>
                      <p className="text-sm text-[#A8A29E]">
                        By {story.author_name} • {formatLocalDate(story.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        story.improvement_percentage >= 70 ? 'bg-green-100 text-green-700' :
                        story.improvement_percentage >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-stone-100 text-stone-700'
                      }`}>
                        {story.improvement_percentage}% better
                      </span>
                    </div>
                  </div>

                  {/* Severity Change */}
                  <div className="flex items-center gap-4 mb-4 p-3 bg-gradient-to-r from-red-50 to-green-50 rounded-lg">
                    <div className="text-center">
                      <span className={`text-2xl font-bold ${getSeverityColor(story.peak_severity)} px-3 py-1 rounded-lg`}>
                        {story.peak_severity}
                      </span>
                      <p className="text-xs text-[#A8A29E] mt-1">Peak</p>
                    </div>
                    <ArrowUpRight className="w-6 h-6 text-green-500 rotate-90" />
                    <div className="text-center">
                      <span className={`text-2xl font-bold ${getSeverityColor(story.current_severity)} px-3 py-1 rounded-lg`}>
                        {story.current_severity}
                      </span>
                      <p className="text-xs text-[#A8A29E] mt-1">Now</p>
                    </div>
                    {story.time_to_improvement && (
                      <div className="ml-auto flex items-center gap-1 text-sm text-[#57534E]">
                        <Clock className="w-4 h-4" />
                        {story.time_to_improvement}
                      </div>
                    )}
                  </div>

                  {/* Treatments */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-[#A8A29E] mb-2">What Helped:</p>
                    <div className="flex flex-wrap gap-2">
                      {story.treatments_that_helped?.slice(0, 5).map((treatment, i) => (
                        <span key={i} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                          {treatment}
                        </span>
                      ))}
                      {story.treatments_that_helped?.length > 5 && (
                        <span className="px-2 py-1 bg-stone-100 text-stone-500 rounded-full text-xs">
                          +{story.treatments_that_helped.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Summary Preview */}
                  <p className={`text-sm text-[#57534E] leading-relaxed ${expandedStory === story.id ? '' : 'line-clamp-3'}`}>
                    {story.journey_summary}
                  </p>

                  {/* Expanded Content */}
                  {expandedStory === story.id && (
                    <div className="mt-4 pt-4 border-t border-stone-100 space-y-4">
                      {story.key_turning_points && (
                        <div>
                          <p className="text-sm font-semibold text-[#2D2D2D] mb-1">Key Turning Points:</p>
                          <p className="text-sm text-[#57534E]">{story.key_turning_points}</p>
                        </div>
                      )}
                      {story.advice_for_parents && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm font-semibold text-amber-800 mb-1">Advice for Other Parents:</p>
                          <p className="text-sm text-amber-700">{story.advice_for_parents}</p>
                        </div>
                      )}
                      {story.initial_symptoms?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-[#2D2D2D] mb-1">Initial Symptoms:</p>
                          <div className="flex flex-wrap gap-1">
                            {story.initial_symptoms.map((symptom, i) => (
                              <span key={i} className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded text-xs">
                                {symptom}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-100">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleLike(story.id)}
                        className={`flex items-center gap-1.5 text-sm ${
                          story.is_liked ? 'text-red-500' : 'text-[#A8A29E] hover:text-red-500'
                        } transition-colors`}
                      >
                        <Heart className={`w-5 h-5 ${story.is_liked ? 'fill-current' : ''}`} />
                        {story.likes_count || 0}
                      </button>
                      <span className="flex items-center gap-1.5 text-sm text-[#A8A29E]">
                        <MessageCircle className="w-5 h-5" />
                        {story.comments_count || 0}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedStory(expandedStory === story.id ? null : story.id)}
                      className="text-primary"
                    >
                      {expandedStory === story.id ? 'Show Less' : 'Read More'}
                      <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${expandedStory === story.id ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Story Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4">
          <Card className="bg-white rounded-xl w-full max-w-2xl my-8">
            <div className="p-6 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Share Your Success Story</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>✕</Button>
              </div>
              <p className="text-sm text-[#57534E] mt-1">Help other families by sharing your journey</p>
            </div>
            
            <form onSubmit={handleSubmitStory} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Story Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Our T&A Surgery Success Story"
                  required
                />
              </div>

              {/* Child Ages */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Age at Onset</label>
                  <Input
                    value={formData.child_age_at_onset}
                    onChange={(e) => setFormData({ ...formData, child_age_at_onset: e.target.value })}
                    placeholder="e.g., 6 years old"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Current Age</label>
                  <Input
                    value={formData.child_current_age}
                    onChange={(e) => setFormData({ ...formData, child_current_age: e.target.value })}
                    placeholder="e.g., 8 years old"
                  />
                </div>
              </div>

              {/* Severity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Peak Severity (1-10)</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.peak_severity}
                    onChange={(e) => setFormData({ ...formData, peak_severity: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Current Severity (1-10)</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.current_severity}
                    onChange={(e) => setFormData({ ...formData, current_severity: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Initial Symptoms */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Initial Symptoms</label>
                <div className="flex flex-wrap gap-2">
                  {symptomOptions.map(symptom => (
                    <button
                      key={symptom}
                      type="button"
                      onClick={() => toggleArrayItem('initial_symptoms', symptom)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.initial_symptoms.includes(symptom)
                          ? 'bg-primary text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {symptom}
                    </button>
                  ))}
                </div>
              </div>

              {/* Treatments */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Treatments That Helped *</label>
                <div className="flex flex-wrap gap-2">
                  {treatmentOptions.map(treatment => (
                    <button
                      key={treatment}
                      type="button"
                      onClick={() => toggleArrayItem('treatments_that_helped', treatment)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.treatments_that_helped.includes(treatment)
                          ? 'bg-green-500 text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {treatment}
                    </button>
                  ))}
                </div>
              </div>

              {/* Journey Summary */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Your Journey Story *</label>
                <textarea
                  value={formData.journey_summary}
                  onChange={(e) => setFormData({ ...formData, journey_summary: e.target.value })}
                  placeholder="Share your family's PANDAS/PANS journey... What happened, how you discovered the diagnosis, what treatments you tried, and how things improved."
                  className="w-full h-32 px-3 py-2 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  required
                />
              </div>

              {/* Key Turning Points */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Key Turning Points</label>
                <textarea
                  value={formData.key_turning_points}
                  onChange={(e) => setFormData({ ...formData, key_turning_points: e.target.value })}
                  placeholder="What were the breakthrough moments in your child's recovery?"
                  className="w-full h-20 px-3 py-2 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Advice */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Advice for Other Parents</label>
                <textarea
                  value={formData.advice_for_parents}
                  onChange={(e) => setFormData({ ...formData, advice_for_parents: e.target.value })}
                  placeholder="What would you tell other parents going through this?"
                  className="w-full h-20 px-3 py-2 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Time to Improvement */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Time to Significant Improvement</label>
                <Input
                  value={formData.time_to_improvement}
                  onChange={(e) => setFormData({ ...formData, time_to_improvement: e.target.value })}
                  placeholder="e.g., 3 months, 1 year"
                />
              </div>

              {/* Anonymous Option */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_anonymous}
                  onChange={(e) => setFormData({ ...formData, is_anonymous: e.target.checked })}
                  className="w-4 h-4 rounded border-stone-300"
                />
                <span className="text-sm text-[#57534E]">Post anonymously</span>
              </label>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90">
                  Share Story
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SuccessStories;
